import { useId, useState } from "react";

import { useTabDrafts } from "@/components/ui/box-draft";
import { selectionOf, type ListBox, type Part, type Result, type ResultLine, type TaskBox } from "@/components/ui/box.schema";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { attendanceApi, type RegisterSession } from "@/lib/api/attendance";
import { ApiError } from "@/lib/api/client";
import { checkCommit, decodeBulkResult, type BulkResult } from "@/lib/api/commitSeams";
import { withheldNotice } from "@/lib/api/sessionPortal";
import { classTitle } from "@/lib/class-name";
import { formatInCourseZone } from "@/lib/session-portal";

import { ALL_SESSIONS_COPY } from "./session-copy";

const copy = ALL_SESSIONS_COPY.bulk;
const change = ALL_SESSIONS_COPY.change;
const imam = { system: "imam" } as const;

/** The note and the email choice, kept in this tab (O7, CASE-038). */
interface BulkDraft {
  note: string;
  email: boolean;
}

/**
 * One send, kept with the draft until its answer, so a reload offers Check on
 * its key and a retry resends the same command with only the failed ids
 * (W1, CASE-029). It never takes text typed since.
 */
interface BulkSend {
  key: string;
  ids: string[];
  versions: Record<string, string>;
  note: string;
  email: boolean;
  sent: BulkDraft;
}

const INITIAL: BulkDraft = { note: "", email: true };

/**
 * Cancel sessions (C07, R6): the scoped bulk Task over All sessions'
 * selection, in the People register's pattern. It rests folded and quiet
 * until sessions are selected, and Review opens it. Each session cancels or
 * fails on its own, a partial result retries only the failed ones under the
 * same key, and the selection clears on a confirmed result and narrows to the
 * failed sessions on a partial one.
 */
export function useCancelSessionsBox({ courseId, timezone, unpublished = false, list, sessions, selected, hidden, onSelect, onChanged }: {
  courseId: string;
  timezone: string;
  /** A draft course tells no one, so no email is offered (CC2); the server enforces it. */
  unpublished?: boolean;
  list: Pick<ListBox<unknown, unknown>, "kind" | "id">;
  /** The register's sessions, to name and version the selected ones. */
  sessions: readonly RegisterSession[];
  selected: readonly string[];
  /** How many selected sessions All sessions' find hides. */
  hidden: number;
  onSelect: (keys: string[]) => void;
  onChanged: () => Promise<unknown>;
}): TaskBox<readonly RegisterSession[]> {
  const kept = useTabDrafts<BulkDraft, BulkSend>("cancel-sessions")({ record: courseId, version: null, initial: INITIAL });
  const { note, email } = kept.value;
  const setNote = (next: string) => kept.set((was) => ({ ...was, note: next }));
  const [result, setResult] = useState<Result | null>(null);
  const id = useId();
  const chosen = sessions.filter((session) => selected.includes(session.id));
  const name = (session: RegisterSession) => classTitle(session, timezone);
  const when = (session: RegisterSession) => (session.starts_at
    ? formatInCourseZone(session.starts_at, timezone, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : session.session_date);
  const settle = (next: Result) => {
    setResult(next);
    return next;
  };
  const lines = (outcomes: BulkResult["succeeded"]): ResultLine[] => outcomes.map((item) => ({ sentence: item.reason.message, ids: [item.id] }));

  const answer = (bulk: BulkResult, delivery: string | null, command: BulkSend): Result => {
    const requestId = command.key;
    if (bulk.state === "confirmed") {
      onSelect([]);
      kept.setPending(null);
      if (JSON.stringify(kept.value) === JSON.stringify(command.sent)) kept.end();
      return { state: "confirmed", cap: copy.cancelled(bulk.succeeded.length), at: new Date().toISOString(), effects: lines(bulk.succeeded).map((line) => line.sentence), remains: delivery ? [delivery] : [] };
    }
    const failed = bulk.failed.map((item) => item.id);
    onSelect(failed);
    return {
      state: "partial",
      cap: copy.partly,
      at: new Date().toISOString(),
      went: lines(bulk.succeeded),
      failed: lines(bulk.failed),
      remains: delivery ? [delivery] : [],
      requestId,
      retry: { verb: copy.retry(failed.length), note: copy.retryNote, run: (key) => send(failed, key, command) },
    };
  };

  const unknown = (command: BulkSend): Result => ({
    state: "unknown",
    cap: change.unknownCap,
    requestId: command.key,
    sentence: copy.unknown,
    check: {
      verb: change.check,
      note: change.checkNote,
      run: async () => {
        let checked: Result;
        try {
          const receipt = await checkCommit(command.key, decodeBulkResult);
          if (receipt.state === "not_committed") {
            // A definite no: said, with the note kept for another send.
            kept.setPending(null);
            checked = { state: "refused", cap: copy.notCancelled, sentence: change.notCommitted, draft: "kept" };
          } else checked = receipt.state === "confirmed" || receipt.state === "partial"
            ? answer(receipt.receipt.result, withheldNotice(receipt.receipt.result.facts?.withheld), command)
            : unknown(command);
        } catch {
          checked = unknown(command);
        }
        if (checked.state !== "unknown") await onChanged();
        return settle(checked);
      },
    },
  });

  /** A first send, or a retry of the failed ids under the first command's key, versions and note (CASE-029). */
  async function send(ids: readonly string[], requestId: string, first?: BulkSend): Promise<Result> {
    const command: BulkSend = first ?? {
      key: requestId,
      ids: [...ids],
      versions: Object.fromEntries(chosen.map((session) => [session.id, session.updated_at])),
      note: note.trim(),
      email: email && !unpublished,
      sent: kept.value,
    };
    kept.setPending(command);
    let next: Result;
    try {
      const outcome = await attendanceApi.cancelSessions(courseId, {
        session_ids: [...ids], expected_updated_at: command.versions, note: command.note, tell_students: command.email,
      }, command.key, Boolean(first));
      await onChanged();
      next = answer(outcome, outcome.delivery.message, command);
    } catch (error) {
      if (error instanceof ApiError && error.status < 500) {
        kept.setPending(null);
        next = { state: "refused", cap: copy.notCancelled, sentence: error.message, draft: "kept" };
      } else next = unknown(command);
    }
    return settle(next);
  }

  // After a reload, a send still unanswered comes back as Not confirmed with Check on its own key (W1).
  const answered: Result = result ?? (kept.pending ? unknown(kept.pending) : { state: "none" });
  // A new selection after a confirmed cancel starts the next one (R6).
  const current: Result = answered.state === "confirmed" && chosen.length ? { state: "none" } : answered;

  const notePart = "cancel-sessions:note";
  // The effects name each selected session and the foot says how many, so no part lists them again (CASE-025).
  const parts: Part[] = [
    {
      id: notePart,
      label: change.note,
      state: note.trim() ? { class: "draft", word: "Written" } : { class: "waiting", word: "Not written" },
      owner: imam,
      fold: "never",
      draft: "browser",
      keep: kept,
      render: () => (
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-note`}>{change.note}</Label>
          <Textarea id={`${id}-note`} value={note} maxLength={200} rows={3} aria-describedby={`${id}-note-help`} onChange={(event) => setNote(event.target.value)} />
          <p id={`${id}-note-help`} className="text-meta text-muted-foreground">{change.noteHelp}</p>
        </div>
      ),
    },
  ];

  return {
    kind: "task",
    id: "cancel-sessions",
    job: copy.job,
    label: copy.label,
    owner: imam,
    read: { status: "ready", value: chosen, readAt: new Date().toISOString() },
    fold: "settled",
    state: (value) => (current.state === "confirmed" ? { class: "done", word: current.cap ?? copy.cancelled(0) }
      : current.state === "partial" ? { class: "failed", word: copy.partly }
        : current.state === "refused" ? { class: "failed", word: copy.notCancelled }
          : current.state === "unknown" ? { class: "reading", word: change.unknownCap }
            : !value.length ? { class: "quiet", word: copy.resting }
              : note.trim() ? { class: "draft", word: copy.ready } : { class: "waiting", word: `${value.length} selected` }),
    detail: (value) => (value.length ? `${value.length} selected` : copy.restingDetail),
    act: chosen.length ? { verb: copy.review(chosen.length), does: "open" } : undefined,
    candidate: chosen.length ? { eligible: true, blocked: null, rank: null } : undefined,
    parts,
    commit: {
      verb: copy.verb(chosen.length),
      cap: copy.cap,
      effects: () => [
        ...chosen.map((session) => ({ id: `cancels:${session.id}`, sentence: change.cancels(name(session), when(session)) })),
        ...(unpublished
          ? [{ id: "reads", sentence: change.readsNoteLater, part: notePart }, { id: "email", sentence: change.noEmail }]
          : [{ id: "reads", sentence: change.readsNote, part: notePart }, { id: "email", sentence: change.email, optional: { checked: email, toggle: () => kept.set((was) => ({ ...was, email: !was.email })) } }]),
      ],
      blockers: () => (note.trim() ? [] : [{ kind: "missing", part: notePart, sentence: change.noteMissing }]),
      run: (requestId) => send(chosen.map((session) => session.id), requestId),
    },
    result: current,
    draft: Boolean(note.trim()),
    over: { selection: selectionOf(list, selected, hidden), leftOut: [] },
  };
}
